import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { useOrg } from '../org/OrgContext.jsx'

// Admin: organization identity + the VISUAL currency label + the three
// notification channel toggles the Notifier consults (email / WhatsApp /
// SMS). Channel credentials live in config.php; these switches decide
// whether a configured channel actually fires.

export function AdminOrganizationPage() {
  const { t } = useTranslation()
  const { user, loading: authLoading } = useAuth()
  const { refresh } = useOrg()

  const [v, setV] = useState(null)
  const [busy, setBusy] = useState(false)
  const [savedAt, setSavedAt] = useState(null)
  const [error, setError] = useState(null)

  const set = (k) => (e) => {
    const val = e.target.type === 'checkbox' ? (e.target.checked ? 1 : 0) : e.target.value
    setV((cur) => ({ ...cur, [k]: val }))
  }

  useEffect(() => {
    if (authLoading || !user || user.role !== 'admin') return
    api.admin.org.get()
      .then((d) => setV(d.organization))
      .catch((e) => setError(e.code || 'server'))
  }, [authLoading, user])

  async function submit(e) {
    e.preventDefault()
    setBusy(true); setError(null); setSavedAt(null)
    try {
      const d = await api.admin.org.update({
        name: v.name,
        email_signature_name: v.email_signature_name,
        email: v.email || null,
        phone: v.phone || null,
        currency_label: v.currency_label,
        notify_email: v.notify_email,
        notify_whatsapp: v.notify_whatsapp,
        notify_sms: v.notify_sms,
        prepay_enabled: v.prepay_enabled,
        prepay_percent: Number(v.prepay_percent),
        prepay_deadline_day: Number(v.prepay_deadline_day),
        invoice_due_days: Number(v.invoice_due_days),
      })
      setV(d.organization)
      setSavedAt(new Date().toLocaleTimeString())
      refresh() // update the shared currency label everywhere
    } catch (err) {
      setError(err.code || 'server')
    } finally { setBusy(false) }
  }

  if (authLoading || (!v && !error)) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!user || user.role !== 'admin') {
    return <main className="container"><p className="error">{t('admin.errors.not_admin')}</p></main>
  }

  return (
    <main className="container">
      <p><Link to="/admin">← {t('admin.title')}</Link></p>
      <h1>{t('admin_org.title')}</h1>
      <p className="muted">{t('admin_org.intro')}</p>

      {error && <p className="error">{t(`admin_org.errors.${error}`, { defaultValue: error })}</p>}
      {savedAt && <p className="muted">{t('account.saved_at', { time: savedAt })}</p>}

      {v && (
        <section className="card form-card">
          <form onSubmit={submit} noValidate>
            <label><span>{t('admin_org.form_name')}</span>
              <input value={v.name || ''} onChange={set('name')} required /></label>
            <label><span>{t('admin_org.form_signature')}</span>
              <input value={v.email_signature_name || ''} onChange={set('email_signature_name')} required /></label>
            <label><span>{t('admin_org.form_email')}</span>
              <input type="email" value={v.email || ''} onChange={set('email')} /></label>
            <label><span>{t('admin_org.form_phone')}</span>
              <input value={v.phone || ''} onChange={set('phone')} /></label>
            <label><span>{t('admin_org.form_currency')} <span className="muted small">({t('admin_org.form_currency_hint')})</span></span>
              <input value={v.currency_label || ''} onChange={set('currency_label')} maxLength={10} required /></label>

            <fieldset>
              <legend>{t('admin_org.section_notifications')}</legend>
              <p className="muted small">{t('admin_org.notifications_hint')}</p>
              <label className="checkbox">
                <input type="checkbox" checked={v.notify_email === 1} onChange={set('notify_email')} />
                <span>{t('admin_org.notify_email')}</span>
              </label>
              <label className="checkbox">
                <input type="checkbox" checked={v.notify_whatsapp === 1} onChange={set('notify_whatsapp')} />
                <span>{t('admin_org.notify_whatsapp')}</span>
              </label>
              <label className="checkbox">
                <input type="checkbox" checked={v.notify_sms === 1} onChange={set('notify_sms')} />
                <span>{t('admin_org.notify_sms')}</span>
              </label>
            </fieldset>

            <fieldset>
              <legend>{t('admin_org.section_prepay')}</legend>
              <p className="muted small">{t('admin_org.prepay_hint')}</p>
              <label className="checkbox">
                <input type="checkbox" checked={v.prepay_enabled === 1} onChange={set('prepay_enabled')} />
                <span>{t('admin_org.prepay_enabled')}</span>
              </label>
              <label><span>{t('admin_org.prepay_percent')} <span className="muted small">({t('admin_org.prepay_percent_hint')})</span></span>
                <input type="number" min={0} max={100} step={1}
                       value={v.prepay_percent ?? 5} onChange={set('prepay_percent')} /></label>
              <label><span>{t('admin_org.prepay_deadline_day')} <span className="muted small">({t('admin_org.prepay_deadline_day_hint')})</span></span>
                <input type="number" min={1} max={28} step={1}
                       value={v.prepay_deadline_day ?? 10} onChange={set('prepay_deadline_day')} /></label>
            </fieldset>

            <fieldset>
              <legend>{t('admin_org.section_invoices')}</legend>
              <p className="muted small">{t('admin_org.invoices_hint')}</p>
              <label><span>{t('admin_org.invoice_due_days')} <span className="muted small">({t('admin_org.invoice_due_days_hint')})</span></span>
                <input type="number" min={1} max={90} step={1}
                       value={v.invoice_due_days ?? 7} onChange={set('invoice_due_days')} /></label>
            </fieldset>

            <div className="form-actions">
              <button type="submit" disabled={busy}>{busy ? t('common.loading') : t('common.save')}</button>
            </div>
          </form>
        </section>
      )}
    </main>
  )
}
